import { ErrorOutlineIcon } from '@ikyucom/design-system-icons-react'
import { RadioButton } from 'macaron-frontend-ui'
import { useCallback } from 'react'
import Image from '~/components/image/Image'
import {
  CC_AMEX_IMAGE_URL,
  CC_DINERS_IMAGE_URL,
  CC_JCB_IMAGE_URL,
  CC_MASTER_IMAGE_URL,
  CC_VISA_IMAGE_URL,
} from '~/lib/constants/constants'
import { useIsPC } from '~/lib/ua'
import { useReserveNavigate } from '~/navigation'
import { css, cva } from '~/styled-system/css'
import { Button } from '~/ui'
import { useSelectedCardInfo } from '../../store/payment'
import { useIsPaymentMethodVisible, usePaymentMethodMessageType } from '../../store/paymentMethod'
import { usePaymentMethod } from '../../store/stateMachine/context'
import { Section } from '../Section'
import { useOnlinePayment } from './useOnlinePayment'
import { useOnsitePayment } from './useOnsitePayment'

export function PaymentMethod() {
  const isVisible = useIsPaymentMethodVisible()
  if (!isVisible) {
    return null
  }
  return (
    <Section title="支払方法">
      <PaymentMethodBody />
    </Section>
  )
}

function PaymentMethodBody() {
  return (
    <div
      className={css({ spaceY: '4', _pc: { border: 'medium', borderRadius: 'default', p: '4' } })}
    >
      <OnsitePayment />
      <OnlinePayment />
    </div>
  )
}

function OnsitePayment() {
  const { selectedPaymentMethod, disabled, checkedOnsite } = useOnsitePayment()
  if (disabled) {
    return null
  }
  return (
    <RadioButton
      name="paymentMethod"
      value="ONSITE"
      checked={selectedPaymentMethod === 'ONSITE'}
      onChange={checkedOnsite}
    >
      現地決済
    </RadioButton>
  )
}

const onlinePaymentStyles = cva({
  base: {
    spaceY: '3',
  },
  variants: {
    state: {
      add: {
        _pc: {
          spaceY: '0',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row',
          alignItems: 'center',
        },
      },
      change: {
        _pc: {
          spaceY: '4',
        },
      },
    },
  },
})

function OnlinePayment() {
  const { selectedPaymentMethod, disabled, radioDisabled, checkedOnline } = useOnlinePayment()
  const card = useSelectedCardInfo()
  const className = onlinePaymentStyles({ state: card === undefined ? 'add' : 'change' })
  return (
    <>
      {/**
       * 表示条件の整理：https://github.com/ikyucom/macaron-frontend/pull/2551
       */}
      <div className={css({ spaceY: '3' })}>
        <div className={className}>
          {disabled ? (
            <p className="text-md">オンラインカード決済</p>
          ) : (
            <RadioButton
              name="paymentMethod"
              value="ONLINE"
              checked={selectedPaymentMethod === 'ONLINE'}
              onChange={checkedOnline}
              disabled={radioDisabled}
            >
              オンラインカード決済
            </RadioButton>
          )}
          <SelectedCard />
        </div>
        <Message />
      </div>
    </>
  )
}

function SelectedCard() {
  const navigate = useReserveNavigate()
  const open = useCallback(() => {
    navigate({ type: 'showCreditCard' })
  }, [navigate])
  const card = useSelectedCardInfo()
  const selected = usePaymentMethod()
  const isPc = useIsPC()
  if (selected === 'ONSITE') {
    return null
  }
  if (card === undefined) {
    return (
      <Button
        color="tertiary"
        onClick={open}
        size={isPc ? 'sm' : 'xs'}
        spread={isPc ? false : true}
      >
        カードを追加
      </Button>
    )
  }
  return (
    <div className="w-full rounded bg-neutral-100 p-2">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex">
            <CardBrand brand={card.brand} />
            <p className="text-sm">**** *{card.maskedNumber}</p>
          </div>
          <p className="text-sm">
            {card.holderName}（{card.goodThru}）
          </p>
        </div>
        <Button color="tertiary" onClick={open} size="xs">
          変更
        </Button>
      </div>
    </div>
  )
}

function CardBrand({ brand }: { brand: string }) {
  const className = 'mr-1 h-5 w-8'
  switch (brand) {
    case 'VISA':
      return <Image src={CC_VISA_IMAGE_URL} className={className} alt="Visa" />
    case 'MASTER':
      return <Image src={CC_MASTER_IMAGE_URL} className={className} alt="MasterCard" />
    case 'DINERS':
      return <Image src={CC_DINERS_IMAGE_URL} className={className} alt="DinersClub" />
    case 'AMEX':
      return <Image src={CC_AMEX_IMAGE_URL} className={className} alt="AmericanExpress" />
    case 'JCB':
      return <Image src={CC_JCB_IMAGE_URL} className={className} alt="JCB" />
    default:
      return null
  }
}

function Message() {
  const messageType = usePaymentMethodMessageType()
  switch (messageType) {
    case 'REQUIRED_PASSWORD':
      return (
        <ErrorMessage>
          <p>
            このご予約は、オンラインカード決済のみご利用いただけます。ご利用には会員登録（パスワード設定）を行ってください。
          </p>
        </ErrorMessage>
      )
    case 'CARD_ONLY':
      return (
        <ErrorMessage>
          このご予約は、オンラインカード決済のみご利用いただけます。ご利用にはカードを追加してください。
        </ErrorMessage>
      )
    case 'NO_REGISTERED_CARD':
      return <ErrorMessage>カードを追加しないと予約を完了できません。</ErrorMessage>
    case 'GMO_MAINTENANCE':
      return (
        <ErrorMessage>
          只今、システムメンテナンスによりオンラインカード決済を停止しております。ご利用の皆さまにはご迷惑をおかけし、大変申し訳ございません。
        </ErrorMessage>
      )
    case 'NO_MESSAGE':
      return null
    default:
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _exhaustiveCheck: never = messageType
  }
}

function ErrorMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        columnGap: '1',
        border: 'light',
        borderColor: 'error.border',
        p: '2',
        fontSize: 'sm',
      })}
    >
      <span>
        <ErrorOutlineIcon className={css({ color: 'error.text' })} />
      </span>
      {children}
    </div>
  )
}
